import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Library, BookmarkCheck, Trash2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function LibraryCard({ refreshKey = 0 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api
      .get("/library")
      .then(({ data }) => setItems(data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [refreshKey]);

  const remove = async (item_id) => {
    try {
      await api.delete(`/library/${item_id}`);
      setItems((prev) => prev.filter((i) => i.item_id !== item_id));
      toast.success("Removed from library");
    } catch (e) {
      toast.error("Remove failed");
    }
  };

  return (
    <div className="glass p-6 flex flex-col gap-3" data-testid="library-card">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 label-eyebrow">
          <Library size={12} className="text-[#ffb067]" /> My Library
        </div>
        <span className="text-[11px] text-white/40 font-mono-oak">
          {items.length} PDF{items.length === 1 ? "" : "s"}
        </span>
      </div>

      {loading ? (
        <div className="space-y-2">
          <div className="h-4 rounded bg-white/5 sheen w-3/4" />
          <div className="h-4 rounded bg-white/5 sheen w-2/4" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-white/45 text-sm leading-relaxed">
          No saved whitepapers yet. Open any <span className="text-[#00FF9D]">Explain Deeper</span> module and
          tap <span className="text-white/70">Save to My Library</span> to store a PDF you can re-read anytime —
          stays inside OAK.
        </div>
      ) : (
        <ul className="space-y-2">
          {items.slice(0, 8).map((it) => (
            <li
              key={it.item_id}
              className="flex items-center justify-between gap-2 border-b border-white/5 last:border-0 pb-2"
              data-testid="library-item"
            >
              <Link
                to={`/library/${it.item_id}`}
                className="flex-1 min-w-0 flex items-center gap-2 group"
              >
                <BookmarkCheck size={14} className="text-[#00FF9D] shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm text-white/85 group-hover:text-white truncate">{it.title}</div>
                  <div className="text-[11px] text-white/40 font-mono-oak truncate">
                    {(it.word_count || 0).toLocaleString()} words · {Math.round((it.size_bytes || 0) / 1024)} KB
                  </div>
                </div>
                <ArrowRight size={12} className="text-white/30 group-hover:text-white/70" />
              </Link>
              <button
                onClick={() => remove(it.item_id)}
                className="text-white/30 hover:text-rose-400 transition p-1.5 rounded"
                data-testid="btn-remove-library"
                title="Remove"
              >
                <Trash2 size={13} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
